import { Wrench } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export const STATUS_CONFIG = {
  estimate: {
    label: "Estimate",
    text: "text-orange-400",
    dot: "bg-orange-400",
    gradient: "from-orange-500 to-amber-400",
  },
  scheduled: {
    label: "Scheduled",
    text: "text-blue-400",
    dot: "bg-blue-400",
    gradient: "from-blue-500 to-blue-400",
  },
  in_progress: {
    label: "In Progress",
    text: "text-amber-400",
    dot: "bg-amber-400",
    gradient: "from-amber-500 to-yellow-400",
    pulse: true,
  },
  deposit_collected: {
    label: "Deposit Collected",
    text: "text-teal-400",
    dot: "bg-teal-400",
    gradient: "from-teal-500 to-cyan-400",
  },
  completed: {
    label: "Completed",
    text: "text-emerald-400",
    dot: "bg-emerald-400",
    gradient: "from-emerald-500 to-green-400",
  },
  cancelled: {
    label: "Cancelled",
    text: "text-red-400",
    dot: "bg-red-400",
    gradient: "from-red-500 to-red-400",
  },
  pending: {
    label: "Pending",
    text: "text-violet-400",
    dot: "bg-violet-400",
    gradient: "from-violet-500 to-purple-400",
  },
};

export const SERVICE_ICON_STYLES = {
  lockout: { color: "text-blue-400", bg: "bg-blue-500/10" },
  rekey: { color: "text-green-400", bg: "bg-green-500/10" },
  installation: { color: "text-purple-400", bg: "bg-purple-500/10" },
  repair: { color: "text-orange-400", bg: "bg-orange-500/10" },
  automotive: { color: "text-cyan-400", bg: "bg-cyan-500/10" },
  emergency: { color: "text-red-400", bg: "bg-red-500/10" },
  commercial: { color: "text-yellow-400", bg: "bg-yellow-500/10" },
  other: { color: "text-muted-foreground", bg: "bg-theme-surface" },
};

export const getServiceIconStyle = (type) =>
  SERVICE_ICON_STYLES[type] || SERVICE_ICON_STYLES.other;

export const getInitials = (name) => {
  if (!name) return "?";
  return name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
};

export const getRelativeTime = (dateStr) => {
  if (!dateStr) return null;
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
  } catch {
    return null;
  }
};

// ─── Dynamic (per-company) statuses — populated by useJobStatuses hook ───
// Default hexes mirror backend SYSTEM_STATUSES; inline styles only apply when
// a company actually customized a color (or the status is custom), so the
// stock tailwind look stays pixel-identical for untouched companies.
const DEFAULT_HEX = {
  "new-lead": "#a855f7", pending: "#8b5cf6", estimate: "#f97316",
  scheduled: "#3b82f6", in_progress: "#f59e0b", deposit_collected: "#14b8a6",
  completed_pending_payment: "#d97706", completed: "#22c55e", cancelled: "#ef4444",
};

let DYNAMIC_STATUSES = {};

export const setDynamicStatuses = (list) => {
  const map = {};
  (list || []).forEach((s) => { map[s.id] = s; });
  DYNAMIC_STATUSES = map;
};

export const getDynamicStatuses = () => DYNAMIC_STATUSES;

export const getStatusCategory = (status) => {
  const d = DYNAMIC_STATUSES[status];
  if (d) return d.category || "active";
  if (status === "completed" || status === "completed_pending_payment") return "completed";
  if (status === "cancelled") return "cancelled";
  return "active";
};

// A job is "finished" when its status is completed-like or cancelled-like
// (custom lifecycle categories included).
export const isFinishedStatus = (status) => getStatusCategory(status) !== "active";

const prettify = (status) =>
  String(status || "").replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const getStatusConfig = (status) => {
  const base = STATUS_CONFIG[status];
  const dyn = DYNAMIC_STATUSES[status];
  if (dyn) {
    const recolored = !base || (dyn.color && dyn.color !== DEFAULT_HEX[status]);
    return {
      label: dyn.label || base?.label || prettify(status),
      color: dyn.color,
      custom: !dyn.system,
      archived: !!dyn.archived,
      category: dyn.category,
      text: base?.text || "",
      dot: base?.dot || "",
      gradient: base?.gradient || "",
      pulse: base?.pulse,
      textStyle: recolored ? { color: dyn.color } : undefined,
      dotStyle: recolored ? { backgroundColor: dyn.color } : undefined,
      barStyle: recolored ? { background: dyn.color, backgroundImage: "none" } : undefined,
    };
  }
  if (base) return base;
  return { ...STATUS_CONFIG.scheduled, label: prettify(status) || "Unknown" };
};

// Strip vehicle info from a job title (titles are composed as
// "Service - YEAR MAKE MODEL"). Removes the CURRENT vehicle string and,
// as a fallback for titles that still embed an OLD vehicle (edited later),
// any trailing " - 2015 Chevrolet Silverado"-style segment.
export const stripVehicleFromTitle = (job) => {
  const hasVehicle = job.vehicle_year || job.vehicle_make || job.vehicle_model;
  if (!hasVehicle) return job.title;
  const vehicleStr = [job.vehicle_year, job.vehicle_make, job.vehicle_model].filter(Boolean).join(" ");
  let t = job.title || "";
  t = t.replace(` - ${vehicleStr}`, "").replace(`- ${vehicleStr}`, "").replace(vehicleStr, "");
  t = t.replace(/\s*[-–]\s*(19|20)\d{2}(\s+\S+){1,4}\s*$/, "");
  t = t.replace(/\s*-\s*$/, "").trim();
  return t || job.title;
};
